/**
 * Warmup gate knobs — one file to edit for a calibration pass. The verdict logic
 * lives in LocalPlacementAsrHandler.startWarmup; calibrate against the distribution
 * fields on runtime.warmupEnded and placement.warmupRouteDecided.
 */

// Wall-clock cap on the once-per-session warmup run. Checked BEFORE each iteration,
// so the final iteration can land past this and still produce a verdict. Graph staging
// shares the cap; graphResolveMs on the summary says how much it consumed.
export const WARMUP_CAP_MS = 5000;

// Minimum iterations before a verdict may be considered.
export const WARMUP_MIN_ITERATIONS = 3;

// Max fractional change between consecutive inferences that counts as stable.
export const WARMUP_CONVERGENCE_THRESHOLD = 0.15;

// Real-time factor a device must beat, applied to the STRIDE: at 1.5 a 200ms stride
// gets 300ms of decode. Above 1.0 lag accumulates within a phrase but resets at
// endPhrase, so the real ceiling is 1 + 3000 / phraseDurationMs (3s backlog cap).
// 1.5 covers ~6s phrases. Watch queueDropCount > 0 for shedding.
export const WARMUP_STREAMING_TARGET_RTF = 1.5;

// Consecutive within-threshold comparisons required before latching a verdict.
export const WARMUP_SLOW_LATCH_COMPARISONS = 2;

// Stride duration in milliseconds for deriving the streaming bar. Exported so tests
// can derive timing values from the constants rather than hardcoding milliseconds.
export const WARMUP_STRIDE_MS = 200;

// The streaming bar: the millisecond ceiling a single stride decode must beat.
// Derived rather than hardcoded so changing WARMUP_STREAMING_TARGET_RTF or the stride
// automatically propagates.
export const WARMUP_STREAMING_BAR_MS = WARMUP_STRIDE_MS * WARMUP_STREAMING_TARGET_RTF;

// BUDGET_EXHAUSTED steady-state rescue (TT-1300). A jittery-but-fast device never
// latches FAST — a single spike poisons the unanimous window and it burns the cap —
// so it settles budgetExhausted despite a steady-state distribution comfortably under
// the bar. On that verdict the router consults the distribution already collected
// instead of blanket-routing to server. These knobs gate ONLY that fallback; they do
// not change the live-loop unanimous-window latch, the bar value, or the cap. The
// admission criterion is looser than FAST (p75-and-thin-tail instead of a unanimous
// window), by design. A false-positive rescue is sticky for the session with no
// demotion path, so the knobs stay conservative: on-bar devices stay on server.

// Minimum steady samples a budgetExhausted run must have before its distribution is
// trusted for a rescue. Thin-sample runs (too few decodes to characterise the device)
// stay on server regardless of how fast the few samples looked.
export const WARMUP_BUDGET_RESCUE_MIN_STEADY_SAMPLES = 10;

// Fraction of the summary's own bar (alreadyFastMs) that p75 must fall under to rescue.
// A factor of the measured bar rather than a hardcoded millisecond value so it tracks
// WARMUP_STREAMING_BAR_MS automatically. Below 1.0 so a device whose p75 sits ON the
// bar is not admitted — that is the shape the live loop is explicit about never
// guessing (LocalPlacementAsrHandler: a device on the bar runs to the cap and reports
// BUDGET_EXHAUSTED). 0.9 is 270ms against the 300ms streaming bar, which still
// clears the ticket's rescue targets (p75 177 and 209).
export const WARMUP_BUDGET_RESCUE_P75_BAR_FACTOR = 0.9;

// Maximum fraction of steady samples allowed over the bar — the tail guard.
// samplesOverBar / steadySamples, not the mean: the rescued population is defined
// by having spikes (that is what poisoned the unanimous window), and a spike moves
// the mean hard while samplesOverBar counts how often the device actually sat over
// the bar. The handler added that field for this question ("0 or steadySamples is
// unanimous; between them is a straddler, and the ratio says how squarely it sits
// on the bar"). 0.2 still clears the ticket's rescue targets (4/39 ≈ 0.10, 4/24 ≈
// 0.17) and rejects a heavy tail.
//
// This bounds spike FREQUENCY, not MAGNITUDE — deliberately. A device with a few
// arbitrarily slow strides (e.g. 2 of 12 at 1.5s) can still clear both this ratio and
// p75, so its mean lands over the bar and a phrase or two sheds audio at the backlog
// cap. That is accepted, not overlooked: bounding magnitude with a mean gate was tried
// (see PR history) and dropped. The occasional shed on a rare-huge-spike device is
// cheaper than server-routing the whole capable population, and any magnitude bound
// tight enough to catch it also risks rejecting the thin-tailed devices this rescue
// exists for. There is no mean gate; magnitude is intentionally unbounded here.
export const WARMUP_BUDGET_RESCUE_MAX_OVER_BAR_RATIO = 0.2;
